import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import Breadcrumbs from '../components/Breadcrumbs';
import '../App.css';
import '../components/SearchResults.css';
import './DetailPage.css';

interface These {
    id: number;
    title?: string;
    titlefr?: string;
    titlear?: string;
    auteur?: string;
    book?: string;
    contenu?: string;
    resume?: string;
}

const ThesesDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [these, setThese] = useState<These | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchThese();
    }, [id, i18n.language]);

    const fetchThese = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/these/${id}?lang=${i18n.language}`);
            setThese(response.data);
        } catch (err) {
            console.error('Error fetching these:', err);
            setError('Erreur lors du chargement de la thèse');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="container mt-5">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !these) {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger">{error || 'Thèse non trouvée'}</div>
                <button className="btn btn-primary" onClick={() => navigate(-1)}>
                    <i className="bi bi-arrow-left me-2"></i>Retour
                </button>
            </div>
        );
    }

    const displayTitle = i18n.language === 'ar'
        ? (these.titlear || these.titlefr || these.title)
        : (these.titlefr || these.title || these.titlear);

    const ebookLink = these.book ? `https://berti-law.net/${these.book}` : null;

    const breadcrumbs = [
        {
            label: t('search.types.theses'),
            onClick: () => navigate(-1)
        },
        { label: displayTitle || t('detail.thesis') }
    ];

    return (
        <div
            className="detail-page no-export"
            onContextMenu={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onKeyDown={(e) => {
                if (e.ctrlKey && (e.key === 'p' || e.key === 'c' || e.key === 'x' || e.key === 's')) {
                    e.preventDefault();
                }
            }}
        >
            <Breadcrumbs items={breadcrumbs} />

            {/* Card 1: Attributes / Metadata */}
            <div className="result-card type-these mb-4 animate-fade-in">
                <div className="result-card-body p-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <span className="premium-badge badge-these mb-2 d-inline-block">
                                <i className="bi bi-mortarboard me-2"></i>{t('search.types.theses')}
                            </span>
                            <h1 className="h3 fw-bold text-dark mb-2 result-title" style={{ fontSize: '1.75rem' }}>
                                {these.title || t('detail.thesis')}
                            </h1>
                        </div>
                        <button
                            className="btn btn-outline-secondary rounded-pill px-4"
                            onClick={() => navigate(-1)}
                        >
                            <i className="bi bi-arrow-left me-2"></i>{t('detail.back')}
                        </button>
                    </div>

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="row g-4">
                        {these.auteur && (
                            <div className="col-md-6">
                                <div className="d-flex align-items-center">
                                    <div className="text-warning me-3">
                                        <i className="bi bi-person fs-4"></i>
                                    </div>
                                    <div>
                                        <h6 className="text-secondary mb-0 text-uppercase small fw-bold" style={{ fontSize: '0.75rem' }}>{t('detail.author')}</h6>
                                        <p className="mb-0 fw-medium text-dark">{these.auteur}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Card 2: Content */}
            {these.contenu && (
                <div className="result-card type-these mb-4 animate-fade-in">
                    <div className="result-card-body p-4">
                        <h4 className="fw-bold mb-4 text-dark border-start border-4 border-warning ps-3">
                            {t('detail.content')}
                        </h4>
                        <div
                            className="content-text"
                            style={{ fontSize: '1.1rem', lineHeight: '1.8', fontFamily: 'Georgia, serif' }}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(these.contenu || '') }}
                        />
                    </div>
                </div>
            )}

            {/* Card 3: Summary */}
            {these.resume && (
                <div className="result-card type-these animate-fade-in">
                    <div className="result-card-body p-4">
                        <h4 className="fw-bold mb-4 text-dark border-start border-4 border-warning ps-3">
                            {t('detail.summary')}
                        </h4>
                        <div
                            className="content-text"
                            style={{ fontSize: '1.1rem', lineHeight: '1.8', fontFamily: 'Georgia, serif' }}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(these.resume || '') }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ThesesDetail;

